import { useCallback, useEffect, useRef, useState } from 'react';
import { readyData } from '@/lib/readParts';
import { loadStripe, type Stripe, type StripeError } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { ApiError } from '@signalvote/api-client';
import {
  ErrorCode,
  WorkOrderCheckoutLineKind,
  WorkOrderStatus,
  type WorkOrderCheckoutLineDto,
  type WorkOrderCheckoutPreview,
  type WorkOrderDto,
} from '@signalvote/shared';
import { Button } from '@/components/ui';
import { useBillingConfig, useWorkOrder, useWorkOrderCheckout, useWorkOrderCheckoutResult } from '@/hooks/queries';

export const formatCents = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

/** A unit price as a receipt writes it: in cents under a dollar ("8¢"), in dollars from there. */
export const formatUnitCents = (cents: number) => (cents < 100 ? `${cents}¢` : formatCents(cents));

/** A line's quantity and unit price in words, e.g. "1,204 × 8¢"; nothing for a single item. */
export function lineQuantity(line: WorkOrderCheckoutLineDto): string | null {
  if (line.quantity <= 1) return null;
  return `${line.quantity.toLocaleString('en-US')} × ${formatUnitCents(line.unitAmountCents)}`;
}

/**
 * Why Pay is held when the preview's total is under the smallest charge Stripe takes, with how many
 * recipients reach it at the per-recipient price; null when the total can be paid.
 */
export function checkoutMinimumNote(preview: WorkOrderCheckoutPreview | undefined): string | null {
  if (!preview?.belowMinimum) return null;
  const unit = preview.lines.find((line) => line.kind === WorkOrderCheckoutLineKind.TextSend)?.unitAmountCents;
  const reach = unit ? ` (${Math.ceil(preview.minimumCents / unit).toLocaleString('en-US')} recipients)` : '';
  return `The smallest text send we can charge is ${formatCents(preview.minimumCents)}${reach}.`;
}

/** Where a paid send is followed: its in-app detail, since the order carries no status link token. */
const statusPath = (id: string) => `/app/work-orders/${id}`;

/** How long the page waits on the webhook that marks the order Paid before it stops asking. */
const PAID_POLL_MS = 2000;
const PAID_POLL_LIMIT_MS = 30_000;

const stripeByKey = new Map<string, Promise<Stripe | null>>();
function stripeFor(publishableKey: string) {
  let stripe = stripeByKey.get(publishableKey);
  if (!stripe) {
    stripe = loadStripe(publishableKey);
    stripeByKey.set(publishableKey, stripe);
  }
  return stripe;
}

/**
 * Where paying stands. `card`: the invoice is raised and the card form is up. `received`: Stripe took
 * the card, and the order turns Paid when the invoice webhook lands.
 */
export type PaymentStage = 'idle' | 'starting' | 'card' | 'confirming' | 'received' | 'paid';

type ConfirmCard = () => Promise<{ error?: StripeError }>;

export interface CardPayment {
  workOrderId: string;
  stage: PaymentStage;
  clientSecret: string | null;
  error: string | null;
  /** The order bar's button: raises the invoice the first time, then confirms the card on that same invoice. */
  action: { label: string; busy: boolean; busyLabel: string; disabled: boolean; onPress: () => void };
  /** Raises the invoice for `order` (the screen's freshest copy) or confirms the card once it's raised. */
  pay: (order?: WorkOrderDto) => void;
  bindCard: (confirm: ConfirmCard | null) => void;
}

interface UseWorkOrderPaymentArgs {
  /** Undefined while the screen still loads it; nothing can be paid until it's there. */
  workOrder: WorkOrderDto | undefined;
  /** What the person sees they'll pay, until the invoice says otherwise. */
  totalCents: number | null;
  /** False while something before paying is unfinished, such as the signature on a first send. */
  ready?: boolean;
  /** A send to a bought list, or a lone list: the list's price the person saw. */
  confirmedVoterDataPriceCents?: number | null;
  /** Told of a refused checkout, after the hook has its message, for a caller that reacts to the code. */
  onRefused?: (error: unknown) => void;
  /** False to leave an Invoiced order's payment where it is, such as while the campaign can't buy it. */
  resume?: boolean;
}

const CHECKOUT_REFUSALS: Partial<Record<string, string>> = {
  [ErrorCode.VoterDataPriceChanged]:
    'The voter list’s price changed since you saw it. Check the new total, then press Pay again.',
  [ErrorCode.CheckoutAlreadyPaid]: 'This order is already paid for. Nothing more was charged.',
  [ErrorCode.TextSendPriceNotConfigured]:
    'Paying for texts isn’t switched on for this site yet. Contact support; nothing has been charged.',
};

/** A refused checkout or checkout preview in plain words, or `fallback` when the server gave no reason. */
export function checkoutErrorMessage(error: unknown, fallback = 'We couldn’t start the payment. Please try again.'): string {
  if (!(error instanceof ApiError) || !error.code) return fallback;
  return CHECKOUT_REFUSALS[error.code] ?? (error.message || fallback);
}

function cardErrorMessage(error: StripeError): string {
  if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) return error.message;
  return 'We couldn’t take the payment. Please try again.';
}

/**
 * Paying a Work Order (a Text send, or a Voter data list on its own) by card in the page. The first Pay
 * raises and finalizes its invoice and mounts the card form on that invoice's payment; every Pay after
 * confirms the card on the same invoice, so a retry after a declined card never raises a second one.
 * Paid itself arrives by webhook: after the card goes through, the order is read again until it says
 * Paid, then the page moves to its status.
 */
export function useWorkOrderPayment({
  workOrder,
  totalCents,
  ready = true,
  confirmedVoterDataPriceCents,
  onRefused,
  resume = true,
}: UseWorkOrderPaymentArgs): CardPayment {
  const navigate = useNavigate();
  const { mutate: checkout, isPending: starting, error: checkoutError, reset } = useWorkOrderCheckout();
  const id = workOrder?.id ?? '';
  const raised = useWorkOrderCheckoutResult(id).data ?? null;
  const [card, setCard] = useState<{ step: 'ready' | 'confirming' | 'received'; error: string | null; since: number }>({
    step: 'ready',
    error: null,
    since: 0,
  });
  const confirmCard = useRef<ConfirmCard | null>(null);
  const bindCard = useCallback((run: ConfirmCard | null) => {
    confirmCard.current = run;
  }, []);

  const received = card.step === 'received';
  const polled = useWorkOrder(workOrder?.id, {
    refetchInterval: received && Date.now() - card.since < PAID_POLL_LIMIT_MS ? PAID_POLL_MS : false,
  });
  const paid = workOrder?.status === WorkOrderStatus.Paid || polled.data?.status === WorkOrderStatus.Paid;

  useEffect(() => {
    if (paid && received) navigate({ to: '/app/work-orders/$workOrderId', params: { workOrderId: id } });
  }, [paid, received, navigate, id]);

  const stage: PaymentStage = paid
    ? 'paid'
    : received
      ? 'received'
      : card.step === 'confirming'
        ? 'confirming'
        : raised
          ? 'card'
          : starting
            ? 'starting'
            : 'idle';

  // A page reloaded on an Invoiced order checks out again, and the server hands back the invoice it raised.
  const resumed = useRef<string | null>(null);
  const invoiced = workOrder?.status === WorkOrderStatus.Invoiced ? workOrder : undefined;
  useEffect(() => {
    if (!resume || !invoiced || raised || resumed.current === invoiced.id) return;
    resumed.current = invoiced.id;
    checkout({ id: invoiced.id, body: { expectedConcurrencyVersion: invoiced.concurrencyVersion } });
  }, [resume, invoiced, raised, checkout]);

  // Stripe returns no confirmation secret when nothing is due, such as a credit balance covering the
  // invoice: it is settled at finalize, so there is no card to take, only the Paid event to wait for.
  useEffect(() => {
    if (raised && raised.clientSecret === null) setCard({ step: 'received', error: null, since: Date.now() });
  }, [raised]);

  const { refetch } = polled;
  const confirm = () => {
    const run = confirmCard.current;
    if (!run) return;
    setCard({ step: 'confirming', error: null, since: 0 });
    run().then(
      (result) => {
        if (result.error) {
          setCard({ step: 'ready', error: cardErrorMessage(result.error), since: 0 });
          return;
        }
        setCard({ step: 'received', error: null, since: Date.now() });
        void refetch();
      },
      () => setCard({ step: 'ready', error: 'We couldn’t take the payment. Please try again.', since: 0 }),
    );
  };

  const pay = (order: WorkOrderDto | undefined = workOrder) => {
    if (stage === 'card') return confirm();
    if (stage !== 'idle' || !order) return;
    reset();
    const variables = { id: order.id, body: { expectedConcurrencyVersion: order.concurrencyVersion, confirmedVoterDataPriceCents } };
    if (onRefused) checkout(variables, { onError: onRefused });
    else checkout(variables);
  };

  const total = raised?.totalCents ?? totalCents;
  const label = stage === 'received' || stage === 'paid' ? 'Paid' : `Pay ${total === null ? '' : formatCents(total)}`.trim();

  return {
    workOrderId: id,
    stage,
    clientSecret: raised?.clientSecret ?? null,
    error: card.error ?? (checkoutError ? checkoutErrorMessage(checkoutError) : null),
    action: {
      label,
      busy: stage === 'starting' || stage === 'confirming',
      busyLabel: stage === 'starting' ? 'Preparing payment…' : 'Paying…',
      disabled: !workOrder || !ready || total === null || stage === 'received' || stage === 'paid',
      onPress: () => pay(),
    },
    pay,
    bindCard,
  };
}

/** Hands the card form's confirm to the payment, so the order bar's button can pay with it. */
function CardForm({ payment }: { payment: CardPayment }) {
  const stripe = useStripe();
  const elements = useElements();
  const { bindCard, workOrderId } = payment;
  useEffect(() => {
    bindCard(
      stripe && elements
        ? () =>
            stripe.confirmPayment({
              elements,
              redirect: 'if_required',
              // Only a card that needs its bank's check (3D Secure) leaves the page, and comes back to the send's status.
              confirmParams: { return_url: `${window.location.origin}${statusPath(workOrderId)}` },
            })
        : null,
    );
    return () => bindCard(null);
  }, [stripe, elements, bindCard, workOrderId]);
  return <PaymentElement />;
}

/**
 * The card form for a Work Order's invoice and what became of the payment. Pass `withButton` where
 * no order bar carries the payment's button.
 */
export function WorkOrderPaymentPanel({ payment, withButton = false }: { payment: CardPayment; withButton?: boolean }) {
  const config = readyData(useBillingConfig());
  const { stage, clientSecret, error, action } = payment;

  return (
    <section role="group" aria-label="Card payment" className="space-y-3 rounded-xl border border-border p-5 text-sm">
      <p className="flex items-center gap-2 font-semibold text-foreground">
        <CreditCard className="h-4 w-4" aria-hidden="true" />
        Card payment
      </p>

      {stage === 'paid' ? (
        <p role="status" className="flex items-center gap-2 text-foreground">
          <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
          Paid. Taking you to your send…
        </p>
      ) : stage === 'received' ? (
        <p role="status" className="flex items-center gap-2 text-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Payment received, confirming…
        </p>
      ) : clientSecret && config ? (
        <Elements stripe={stripeFor(config.publishableKey)} options={{ clientSecret }}>
          <CardForm payment={payment} />
        </Elements>
      ) : clientSecret ? (
        <p role="status" className="text-muted-foreground">
          Loading the card form…
        </p>
      ) : (
        <p className="text-muted-foreground">Press Pay to enter your card. Nothing is charged until you confirm it.</p>
      )}

      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}

      {withButton && stage !== 'paid' && stage !== 'received' && (
        <Button type="button" className="w-full" disabled={action.disabled || action.busy} onClick={action.onPress}>
          {action.busy ? action.busyLabel : action.label}
        </Button>
      )}
    </section>
  );
}
